import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import {
  Eye,
  EyeOff,
  Building,
  User,
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  Check,
  Mail,
  KeyRound,
  Shield,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Icon } from "@iconify/react"; // npm i @iconify/react
import InputField from "./components/InputComponent/InputField";
import axios from "axios";
import ilaLogo from "./assets/ila-logo.png";
import ourDeptLogo from "./assets/ourdept-logo.png";
import "./Login.scss";

/* Products shown in the top launcher come from
   GET {BACKEND}/api/public/products/login-page/all
   Response: { status, message, data: [{ productName, description, productCode,
   productLink, productIconUrl, productType }] }
   The API has no colour fields, so each product gets a stable accent colour
   derived from its productCode. Works for any number of products. */
const PRODUCTS_URL = "/api/public/products/login-page/all";

const hashString = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
};

// Golden-angle hue spacing keeps neighbouring products visually distinct.
const productColors = (key) => {
  const hue = (hashString(key) * 137.508) % 360;
  return {
    color: `hsl(${hue.toFixed(0)} 65% 42%)`,
    softColor: `hsl(${hue.toFixed(0)} 85% 93%)`,
  };
};

const normalizeHost = (host = "") => host.toLowerCase().replace(/^www\./, "");

const hostOf = (url = "") => {
  try {
    return normalizeHost(new URL(url, window.location.origin).hostname);
  } catch {
    return "";
  }
};

// True when the product is the app the user is currently on. Matches either
// the product's own code (REACT_APP_PRODUCT_ID, the same value sent as
// X-ProductID on login) or its link's hostname vs. the current URL.
const isCurrentProduct = (product) => {
  const currentCode = process.env.REACT_APP_PRODUCT_ID;
  if (currentCode && product.productCode === currentCode) return true;
  const productHost = hostOf(product.productLink);
  return (
    !!productHost && productHost === normalizeHost(window.location.hostname)
  );
};

const isImageUrl = (value = "") =>
  /^(https?:)?\/\/|^\/|^data:image\//i.test(value);

function ProductIcon({ product }) {
  const { productIconUrl, productName } = product;
  if (productIconUrl && isImageUrl(productIconUrl)) {
    return (
      <img className="lg-app__img" src={productIconUrl} alt="" loading="lazy" />
    );
  }
  if (productIconUrl) {
    // Iconify id, e.g. "icon-park:setting-two"
    return <Icon icon={productIconUrl} width="22" height="22" />;
  }
  // No icon supplied: fall back to the first letter of the name.
  return (
    <span className="lg-app__letter">
      {(productName || "?").trim().charAt(0).toUpperCase()}
    </span>
  );
}

export default function ModernLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("login");
  // forgotStep: "request" (enter email+company) | "verify" (enter OTP+new password)
  const [forgotStep, setForgotStep] = useState("request");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    tenantId: "",
    username: "",
    password: "",
    email: "",
    newPassword: "",
    confirmPassword: "",
    otp: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const employeeSlides = [
    {
      title: "Are you a Team Member?",
      content: "Hey there! Ready to crush today's tasks?",
      subtitle: "Your work, your progress — all in one place.",
      features: [
        "✅ View and update your assigned tasks",
        "📁 Access project files and timelines",
        "💬 Collaborate with your team in real-time",
      ],
      footer: "Work smarter, not harder. Stay on track, every day.",
      visual: "tasks",
    },
  ];

  const hrSlides = [
    {
      title: "Are you a Project Manager?",
      content: "✨ Log Back In — Your Projects Are Waiting",
      features: [
        "🗂 Plan and organize sprints with ease",
        "📊 Track team progress and milestones",
        "⏰ Manage deadlines and resource allocation",
        "🛡 Ensure delivery with full visibility",
      ],
      footer: "Trusted by project leads across industries.",
      subtitle: "Login to take full control of your projects.",
      visual: "sprint",
    },
    {
      title: "Need Help?",
      content: "Our support team is available 24/7 to assist you.",
      icon: "💬",
      visual: "chat",
    },
  ];

  const currentSlides = [...employeeSlides, ...hrSlides];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Product launcher: fetched once on mount.
  useEffect(() => {
    const controller = new AbortController();

    const fetchProducts = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}${PRODUCTS_URL}`,
          { signal: controller.signal },
        );
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setProducts(
          list
            .filter((p) => p && p.productName && !isCurrentProduct(p))
            .map((p) => ({
              ...p,
              ...productColors(p.productCode || p.productName),
            })),
        );
      } catch (err) {
        if (axios.isCancel(err) || err.name === "CanceledError") return;
        console.error("Failed to load products:", err.message);
        setProducts([]); // launcher simply stays hidden; login still works
      } finally {
        if (!controller.signal.aborted) setProductsLoading(false);
      }
    };

    fetchProducts();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
    }, 7000);
    return () => clearTimeout(timer);
  }, [currentSlide, currentSlides.length]);

  useEffect(() => {
    const isSsoRedirect = sessionStorage.getItem("sso_redirect");
    const tenantId = sessionStorage.getItem("tenantId");

    if (!isSsoRedirect || !tenantId) return;

    // ✅ Clear both flags immediately
    sessionStorage.removeItem("sso_redirect");
    const redirectTo =
      sessionStorage.getItem("sso_redirect_to") || "/dashboard";
    sessionStorage.removeItem("sso_redirect_to");

    const autoLogin = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/refresh-token`,
          {},
          {
            headers: {
              "X-TenantID": tenantId,
              "X-ProductID": process.env.REACT_APP_PRODUCT_ID,
            },
            withCredentials: true,
          },
        );

        if (response.status === 200) {
          const jwtToken =
            response.headers["authorization"] ||
            response.headers["Authorization"];
          if (jwtToken) sessionStorage.setItem("jwtToken", jwtToken);
          login(response.data);
          // ✅ Go to intended page, not always /dashboard
          setTimeout(() => navigate(redirectTo), 100);
        }
      } catch (err) {
        setError("Auto-login failed. Please sign in manually.");
        setLoading(false);
      }
    };

    autoLogin();
  }, [login, navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  // ─── LOGIN ────────────────────────────────────────────────────────────────
  const handleSubmit = async (event) => {
    event.preventDefault();
    clearMessages();

    const { username, password, tenantId } = formData;
    if (!username || !password || !tenantId) {
      setError("Company ID, Username, and Password are required");
      return;
    }

    setLoading(true);
    const token = btoa(`${username}:${password}`);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/employeeLogin`,
        {
          method: "POST",
          headers: {
            Authorization: `Basic ${token}`,
            "X-TenantID": tenantId,
            "X-ProductID": process.env.REACT_APP_PRODUCT_ID,
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        if (response.status === 409) {
          const jwtToken = response.headers.get("Authorization");
          if (jwtToken) sessionStorage.setItem("jwtToken", jwtToken);
          sessionStorage.setItem("tenantId", tenantId);
          const errorData = await response.json();
          throw new Error(errorData.message || "Conflict occurred");
        } else if (response.status === 401 || response.status === 400) {
          throw new Error("Invalid credentials");
        } else {
          throw new Error("Something went wrong. Please try again.");
        }
      }

      const data = await response.json();
      if (data) {
        const jwtToken = response.headers.get("Authorization");
        if (jwtToken) sessionStorage.setItem("jwtToken", jwtToken);
        sessionStorage.setItem("tenantId", tenantId);
        login(data);
        setSuccess("Login successful!");
        setTimeout(() => navigate("/dashboard"), 1000);
      } else {
        setError("Invalid credentials or unexpected response.");
      }
    } catch (error) {
      setError(error.message || "Login failed, please try again later");
    } finally {
      setLoading(false);
    }
  };

  // ─── FORGOT PASSWORD: STEP 1 — Request OTP ───────────────────────────────
  const handleRequestOtp = async (event) => {
    event.preventDefault();
    clearMessages();

    const { email, tenantId } = formData;
    if (!email || !tenantId) {
      setError("Company ID and Email Address are required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/forgot-password/request-otp`,
        { email, companyId: tenantId },
      );

      if (response.status === 200) {
        setSuccess("OTP sent to your email. Please check your inbox.");
        setForgotStep("verify");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to send OTP. Please check your details and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── FORGOT PASSWORD: STEP 2 — Verify OTP & Reset Password ──────────────
  const handleResetPassword = async (event) => {
    event.preventDefault();
    clearMessages();

    const { email, tenantId, otp, newPassword, confirmPassword } = formData;

    if (!otp || !newPassword || !confirmPassword) {
      setError("OTP, New Password, and Confirm Password are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/forgot-password/verify-otp`,
        { email, companyId: tenantId, otp, newPassword },
      );

      if (response.status === 200) {
        setSuccess(
          "Password reset successfully! Please login with your new password.",
        );
        setTimeout(() => switchToLogin(), 2000);
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to reset password. Please check your OTP and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── NAVIGATION HELPERS ───────────────────────────────────────────────────
  const resetForgotPassword = () => {
    setFormData((prev) => ({
      ...prev,
      email: "",
      newPassword: "",
      confirmPassword: "",
      otp: "",
    }));
    setForgotStep("request");
  };

  const switchToForgotPassword = () => {
    clearMessages();
    setActiveTab("forgot");
    resetForgotPassword();
  };

  const switchToLogin = () => {
    clearMessages();
    setActiveTab("login");
    resetForgotPassword();
  };

  const goBackToRequestStep = () => {
    clearMessages();
    setForgotStep("request");
    setFormData((prev) => ({
      ...prev,
      otp: "",
      newPassword: "",
      confirmPassword: "",
    }));
  };

  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
  const prevSlide = () =>
    setCurrentSlide(
      (prev) => (prev - 1 + currentSlides.length) % currentSlides.length,
    );

  const renderScene = (type) => {
    if (type === "tasks")
      return (
        <div className="win">
          <div className="win__bar">
            <i />
            <i />
            <i />
            <span>My Tasks</span>
          </div>
          <div className="win__body win__body--tasks">
            {[
              ["To do", "#94a3b8", 2],
              ["In progress", "#4f46e5", 2],
              ["Done", "#16a34a", 1],
            ].map(([label, c, count], col) => (
              <div className="card kcol" key={label} style={{ "--c": c }}>
                <div className="kcol__head">
                  <span className="dot" />
                  {label}
                </div>
                {Array.from({ length: count }).map((_, r) => (
                  <div
                    className={`kb ${col === 1 && r === 0 ? "kb--out" : ""}`}
                    key={r}
                    style={{ "--n": col * 2 + r }}
                  >
                    <i className="sk" style={{ "--w": "85%" }} />
                    <i className="sk" style={{ "--w": "55%" }} />
                    {col === 1 && r === 0 && <i className="kb__bar" />}
                    <div className="kb__foot">
                      <em className="tag" />
                      <b className="av av--s" />
                    </div>
                  </div>
                ))}
                {col === 2 && (
                  <div className="kb kb--in">
                    <i className="sk" style={{ "--w": "85%" }} />
                    <i className="sk" style={{ "--w": "55%" }} />
                    <div className="kb__foot">
                      <em className="tag" />
                      <b className="av av--s" />
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="toast">
              <Check /> Task completed
            </div>
          </div>
        </div>
      );
    if (type === "sprint")
      return (
        <div className="win">
          <div className="win__bar">
            <i />
            <i />
            <i />
            <span>Sprint Overview</span>
          </div>
          <div className="win__body win__body--sprint">
            <div className="card bd">
              <i className="sk" style={{ "--w": "40%" }} />
              <svg viewBox="0 0 100 50" preserveAspectRatio="none">
                {[12, 25, 38].map((y) => (
                  <line key={y} x1="0" y1={y} x2="100" y2={y} />
                ))}
                <path className="ideal" d="M0 6 L100 46" pathLength="1" />
                <path
                  className="actual"
                  d="M0 6 L14 13 L28 15 L42 25 L58 27 L72 37 L100 43"
                  pathLength="1"
                />
              </svg>
            </div>
            <div className="card donut">
              <svg viewBox="0 0 100 100">
                <circle className="bg" cx="50" cy="50" r="40" />
                <circle className="fg" cx="50" cy="50" r="40" />
              </svg>
            </div>
            <div className="card gantt">
              {[
                [0, 45, "#4f46e5"],
                [20, 40, "#059669"],
                [38, 34, "#ea580c"],
                [58, 36, "#7c3aed"],
              ].map(([l, w, c], n) => (
                <div className="gantt__row" key={n}>
                  <i className="sk" />
                  <div className="gantt__track">
                    <span
                      style={{
                        "--l": `${l}%`,
                        "--w": `${w}%`,
                        "--c": c,
                        "--n": n,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="toast">
              <Check /> Milestone reached
            </div>
          </div>
        </div>
      );
    return (
      <div className="win">
        <div className="win__bar">
          <i />
          <i />
          <i />
          <span>Support</span>
        </div>
        <div className="win__body win__body--chat">
          <div className="card list">
            {[0, 1, 2].map((n) => (
              <div className={`li ${n === 0 ? "on" : ""}`} key={n}>
                <b className="av" />
                <div>
                  <i className="sk" style={{ "--w": "80%" }} />
                  <i className="sk" style={{ "--w": "50%" }} />
                </div>
              </div>
            ))}
          </div>
          <div className="card thread">
            <p className="q">Where do I find my sprint deadlines?</p>
            <div className="typing">
              <i />
              <i />
              <i />
            </div>
            <p className="a">Open Timeline, then Deadlines. Done.</p>
            <div className="input">
              <i className="sk" style={{ "--w": "45%" }} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`lg-page ${isVisible ? "lg-page--in" : ""}`}>
      {/* ---------- Top: software launcher ---------- */}
      <header className="lg-top">
        {(productsLoading || products.length > 0) && (
          <nav className="lg-apps__grid" aria-label="Your software">
            {productsLoading
              ? [0, 1, 2, 3].map((n) => (
                  <span
                    key={n}
                    className="lg-app lg-app--skeleton"
                    aria-hidden="true"
                  >
                    <span className="lg-app__icon" />
                    <i className="sk" style={{ "--w": "60%", margin: 0 }} />
                  </span>
                ))
              : products.map((p, i) => (
                  <a
                    key={p.productCode || p.productName}
                    href={p.productLink}
                    title={p.description || p.productName}
                    className="lg-app lg-reveal"
                    style={{
                      "--i": Math.min(i, 8),
                      "--c": p.color,
                      "--soft": p.softColor,
                    }}
                  >
                    <span className="lg-app__icon">
                      <ProductIcon product={p} />
                    </span>
                    <span className="lg-app__name">{p.productName}</span>
                    <ArrowUpRight className="lg-app__go w-4 h-4" />
                  </a>
                ))}
          </nav>
        )}
      </header>

      <main className="lg-main">
        {/* ---------- Left: sign in / reset ---------- */}
        <section className="lg-left lg-reveal" style={{ "--i": 4 }}>
          <div className="lg-left__body">
            <div className="lg-pane" key={`${activeTab}-${forgotStep}`}>
              {activeTab !== "forgot" ? (
                <>
                  <img
                    className="lg-brand"
                    src={ourDeptLogo}
                    alt="OurDept.com - One platform. Every department. Total control."
                  />
                  <h2>Welcome Back</h2>
                  <p className="lg-sub">Enter your credentials to continue</p>
                  {error && (
                    <div className="lg-msg bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="lg-msg bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {success}
                    </div>
                  )}
                  <form className="lg-fields" onSubmit={handleSubmit}>
                    <InputField
                      label="Company ID"
                      type="text"
                      value={formData.tenantId}
                      onChange={(e) =>
                        handleInputChange("tenantId", e.target.value)
                      }
                      placeholder="Enter your company ID"
                      icon={Building}
                    />
                    <InputField
                      label="Username"
                      type="text"
                      value={formData.username}
                      onChange={(e) =>
                        handleInputChange("username", e.target.value)
                      }
                      placeholder="Enter your username"
                      icon={User}
                    />
                    <div className="space-y-2">
                      <label>Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) =>
                            handleInputChange("password", e.target.value)
                          }
                          className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          aria-label="Toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <button type="submit" disabled={loading} className="lg-btn">
                      {loading ? "Signing in..." : "Sign In"}
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={switchToForgotPassword}
                        className="lg-link"
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div
                    className="lg-steps"
                    aria-label="Reset password progress"
                  >
                    <b className={forgotStep === "request" ? "on" : "done"}>
                      1
                    </b>
                    <i className={forgotStep === "verify" ? "on" : ""} />
                    <b className={forgotStep === "verify" ? "on" : ""}>2</b>
                  </div>
                  <h2>
                    {forgotStep === "request" ? "Reset Password" : "Enter OTP"}
                  </h2>
                  <p className="lg-sub">
                    {forgotStep === "request"
                      ? "We'll send a one-time password to your email"
                      : `OTP sent to ${formData.email}`}
                  </p>
                  {error && (
                    <div className="lg-msg bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="lg-msg bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {success}
                    </div>
                  )}
                  {/* STEP 1: request OTP */}
                  {forgotStep === "request" && (
                    <form className="lg-fields" onSubmit={handleRequestOtp}>
                      <InputField
                        label="Company ID"
                        type="text"
                        value={formData.tenantId}
                        onChange={(e) =>
                          handleInputChange("tenantId", e.target.value)
                        }
                        placeholder="Enter your company ID"
                        icon={Building}
                      />
                      <InputField
                        label="Email Address"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        placeholder="Enter your registered email"
                        icon={Mail}
                      />
                      <button
                        type="submit"
                        disabled={loading}
                        className="lg-btn"
                      >
                        {loading ? "Sending OTP..." : "Send OTP"}
                        <ArrowRight className="w-5 h-5" />
                      </button>
                      <div className="text-center">
                        <button
                          type="button"
                          onClick={switchToLogin}
                          className="lg-link inline-flex items-center"
                        >
                          <ArrowLeft className="w-4 h-4 mr-1" />
                          Back to Sign In
                        </button>
                      </div>
                    </form>
                  )}
                  {/* STEP 2: verify OTP + new password */}
                  {forgotStep === "verify" && (
                    <form className="lg-fields" onSubmit={handleResetPassword}>
                      <div className="space-y-2">
                        <label>One-Time Password (OTP)</label>
                        <div className="relative">
                          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                          <input
                            type="text"
                            value={formData.otp}
                            onChange={(e) =>
                              handleInputChange("otp", e.target.value)
                            }
                            className="w-full pl-10 pr-3 py-2.5 text-slate-800 placeholder-slate-400"
                            placeholder="Enter OTP"
                            maxLength={6}
                          />
                        </div>
                      </div>
                      <div className="lg-row2">
                        <div className="space-y-2">
                          <label>New Password</label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              value={formData.newPassword}
                              onChange={(e) =>
                                handleInputChange("newPassword", e.target.value)
                              }
                              className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                              placeholder="New password"
                            />
                            <button
                              type="button"
                              aria-label="Toggle password visibility"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              {showNewPassword ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label>Confirm Password</label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              value={formData.confirmPassword}
                              onChange={(e) =>
                                handleInputChange(
                                  "confirmPassword",
                                  e.target.value,
                                )
                              }
                              className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                              placeholder="Confirm password"
                            />
                            <button
                              type="button"
                              aria-label="Toggle password visibility"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="lg-btn"
                      >
                        {loading ? "Resetting..." : "Reset Password"}
                        <ArrowRight className="w-5 h-5" />
                      </button>
                      <div className="lg-links-row">
                        <button
                          type="button"
                          onClick={goBackToRequestStep}
                          className="lg-link inline-flex items-center"
                        >
                          <ArrowLeft className="w-4 h-4 mr-1" />
                          Change Email
                        </button>
                        <button
                          type="button"
                          onClick={handleRequestOtp}
                          disabled={loading}
                          className="lg-link lg-link--muted"
                        >
                          Resend OTP
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
          {/* ---------- Footer: animated logo (under the left column only) ---------- */}
          <footer className="lg-foot">
            <a
              className="lg-logo"
              href="https://www.indialeadershipawards.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="India Leadership Awards"
              style={{ "--logo": `url(${ilaLogo})` }}
            >
              <span className="lg-logo__glow" aria-hidden="true" />
              <span className="lg-logo__art">
                <img src={ilaLogo} alt="India Leadership Awards" />
                <span className="lg-logo__shine" aria-hidden="true" />
                <span
                  className="lg-star"
                  style={{ "--x": "27%", "--y": "4%", "--d": "0s" }}
                />
                <span
                  className="lg-star"
                  style={{ "--x": "31.2%", "--y": "8%", "--d": "0.9s" }}
                />
                <span
                  className="lg-star"
                  style={{ "--x": "23.2%", "--y": "8%", "--d": "1.7s" }}
                />
              </span>
              {[8, 20, 32, 44, 56, 68, 80, 92].map((x, i) => (
                <span
                  key={x}
                  className="lg-spark"
                  style={{ "--x": `${x}%`, "--d": `${(i * 0.75) % 5}s` }}
                />
              ))}
            </a>
          </footer>
        </section>

        {/* ---------- Right: product preview ---------- */}
        <aside className="lg-right lg-reveal" style={{ "--i": 5 }}>
          {activeTab === "forgot" ? (
            <div className="lg-recover">
              <div className="lg-shield">
                <Shield className="w-10 h-10" />
              </div>
              <h1>Secure Recovery</h1>
              <p>
                We'll help you regain access to your account safely and securely
              </p>
              <div className="lg-note">
                {forgotStep === "request"
                  ? "Enter your company ID and email to receive a one-time password."
                  : "Enter the OTP from your email along with your new password."}
              </div>
            </div>
          ) : (
            <>
              <div className="lg-slide" key={currentSlide}>
                <div className="lg-copy">
                  <h2>{currentSlides[currentSlide].title}</h2>
                  <p className="lead">{currentSlides[currentSlide].content}</p>
                  {currentSlides[currentSlide].subtitle && (
                    <p>{currentSlides[currentSlide].subtitle}</p>
                  )}
                  {currentSlides[currentSlide].features && (
                    <ul className="lg-feats">
                      {currentSlides[currentSlide].features.map(
                        (feature, index) => (
                          <li key={index} style={{ "--n": index }}>
                            <Check />
                            {feature}
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                  {currentSlides[currentSlide].footer && (
                    <p className="lg-foot-note">
                      {currentSlides[currentSlide].footer}
                    </p>
                  )}
                </div>
                <div className="lg-winwrap">
                  {renderScene(currentSlides[currentSlide].visual)}
                </div>
              </div>
              <div className="lg-ctrl">
                <button
                  type="button"
                  className="lg-arrow"
                  onClick={prevSlide}
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="lg-dots">
                  {currentSlides.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Go to slide ${index + 1}`}
                      onClick={() => setCurrentSlide(index)}
                      className={`lg-dot ${index === currentSlide ? "is-active" : ""}`}
                    >
                      <span />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="lg-arrow"
                  onClick={nextSlide}
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </aside>
      </main>
    </div>
  );
}
